import { watch } from 'vue';
import { createI18n } from 'vue-i18n';
import de from './locales/de.json';
import en from './locales/en.json';
import fr from './locales/fr.json';
import nl from './locales/nl.json';

export const AVAILABLE_LOCALES = ['nl', 'en', 'de', 'fr'];

export const normalizeLocale = (locale) => (
  String(locale || '').toLowerCase().split('-')[0]
);

export const getBrowserLocale = () => {
  if (typeof navigator === 'undefined') {
    return 'nl';
  }

  const browserLocales = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];

  for (const locale of browserLocales) {
    const normalizedLocale = normalizeLocale(locale);
    if (AVAILABLE_LOCALES.includes(normalizedLocale)) {
      return normalizedLocale;
    }
  }

  return 'nl';
};

const LOCALE_OVERRIDE_SESSION_KEY = 'ui-v2-locale-override';

const i18n = createI18n({
  legacy: false,
  locale: getBrowserLocale(),
  fallbackLocale: 'nl',
  messages: {
    de,
    en,
    fr,
    nl,
  },
});

// Screen readers and the browser's translate offer read the page language from `<html lang>`.
if (typeof document !== 'undefined') {
  watch(i18n.global.locale, (locale) => {
    document.documentElement.lang = locale;
  }, { immediate: true });
}

// Set once from a `?lang=` query param, this wins over the profile language for the rest of
// the browser tab session — that's how a research link forces the respondent's UI language even
// though most in-research navigation never repeats the query param.
export const applyLocaleOverrideFromQuery = (langParam) => {
  if (typeof window === 'undefined') {
    return null;
  }
  const locale = normalizeLocale(langParam);
  if (!AVAILABLE_LOCALES.includes(locale)) {
    return null;
  }
  sessionStorage.setItem(LOCALE_OVERRIDE_SESSION_KEY, locale);
  i18n.global.locale.value = locale;
  return locale;
};

export const applyStoredLocaleOverride = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  const locale = normalizeLocale(sessionStorage.getItem(LOCALE_OVERRIDE_SESSION_KEY));
  if (!AVAILABLE_LOCALES.includes(locale)) {
    return null;
  }
  i18n.global.locale.value = locale;
  return locale;
};

export const clearLocaleOverride = () => {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem(LOCALE_OVERRIDE_SESSION_KEY);
};

export default i18n;
